declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
  }
}

/** GTM pushes are independent of the Mixpanel flag and of initAnalytics(). */
export function pushDataLayer(
  event: string,
  extra?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  window.dataLayer ??= [];
  window.dataLayer.push({ event, ...extra });
}

/**
 * How long a push may hold the caller before it navigates away. GTM's own
 * `eventTimeout` only starts once the container has processed the push, so a
 * local timer bounds the wait even while gtm.js is still loading.
 */
export const DATA_LAYER_WAIT_MS = 1000;

/** True once index.html's GTM bootstrap has queued `gtm.start`. */
function gtmBootstrapped(): boolean {
  return (
    Array.isArray(window.dataLayer) &&
    window.dataLayer.some(
      (entry) => entry && typeof entry === "object" && "gtm.start" in entry,
    )
  );
}

/**
 * Pushes an event and resolves once every tag it triggers has fired, or after
 * `timeoutMs` — whichever comes first. For pushes made right before a full
 * navigation (`loginWithRedirect`): the Ads conversion tag issues its own
 * request after the GA4 beacon, and an immediate unload can cut it off.
 * Without a GTM bootstrap on the page there is nothing to wait for, so the
 * push is synchronous and the promise resolves at once.
 */
export function pushDataLayerAndWait(
  event: string,
  extra?: Record<string, unknown>,
  timeoutMs: number = DATA_LAYER_WAIT_MS,
): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  window.dataLayer ??= [];

  if (!gtmBootstrapped()) {
    window.dataLayer.push({ event, ...extra });
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(settle, timeoutMs);
    window.dataLayer.push({
      event,
      ...extra,
      eventCallback: settle,
      eventTimeout: timeoutMs,
    });
  });
}

/**
 * SHA-256 of a Google-normalized email (trimmed, lowercased, dots dropped
 * from gmail local parts), for enhanced conversions: the Ads tag matches a
 * signup on the hashed address when no click cookie survived. Hashing here
 * keeps the raw address out of the dataLayer. Null when the address is
 * unusable or SubtleCrypto is unavailable (insecure context).
 */
export async function hashEmailForAds(
  email: string | null | undefined,
): Promise<string | null> {
  if (!email) return null;
  const normalized = normalizeEmailForAds(email);
  if (!normalized) return null;
  try {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(normalized),
    );
    return Array.from(new Uint8Array(digest), (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");
  } catch {
    return null;
  }
}

export function normalizeEmailForAds(email: string): string | null {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;
  let local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (domain === "gmail.com" || domain === "googlemail.com") {
    local = local.replace(/\./g, "");
  }
  return `${local}@${domain}`;
}
